/**
 * Role 12 — Statement of Profit or Loss — calculated totals.
 *
 * Extracted verbatim from the former single-class `CalculationService`, which held
 * all 13 sheets' total calculations in one 5,557-line file. Each function is pure and
 * self-contained (no shared state, no `this`), so the split is a move, not a rewrite.
 * `CalculationService` remains the injectable entry point and delegates here.
 */

/**
 * Calculate all Role12 (310000 - Statement of Profit or Loss) totals based on form data
 *
 * @param formData - The form data object with control values (format: qname[member][period])
 * @param xbrlPrefix - The XBRL prefix ('ifrs-full' for MFRS, 'ifrs-smes' for MPERS)
 * @returns Object containing only the calculated total fields
 */
export function calculateTotals_310000(
  formData: Record<string, any>,
  xbrlPrefix: 'ifrs-full' | 'ifrs-smes',
): Record<string, any> {
  const calculatedTotals: Record<string, any> = {};

  // Helper function to get field value from formData
  const getFieldValue = (qname: string, suffix: string): number => {
    const value = formData[qname + suffix];

    // Handle null, undefined, or empty string
    if (value === null || value === undefined || value === '') {
      return 0;
    }

    // Convert to number if it's a string
    const numValue = typeof value === 'number' ? value : parseFloat(value);

    // Return 0 if conversion resulted in NaN
    return isNaN(numValue) ? 0 : numValue;
  };

  // Helper function to set calculated value
  const setValue = (qname: string, suffix: string, value: number): void => {
    const controlName = qname + suffix;
    calculatedTotals[controlName] = value;
  };

  // Determine prefixes based on MFRS or MPERS
  const prefix = xbrlPrefix;
  const customPrefix = prefix === 'ifrs-full' ? 'ssmt-mfrs' : 'ssmt-mpers';

  // Calculate for all 4 columns
  const suffixes = [
    '[Consolidated][CurrentYear]',
    '[Consolidated][PreviousYear]',
    '[Separate][CurrentYear]',
    '[Separate][PreviousYear]',
  ];

  suffixes.forEach((suffix) => {
    // 1. Gross Profit = Revenue - Cost of Sales
    const revenue = getFieldValue(`${prefix}:Revenue`, suffix);
    const costOfSales = getFieldValue(`${prefix}:CostOfSales`, suffix);
    const grossProfit = revenue - costOfSales;
    setValue(`${prefix}:GrossProfit`, suffix, grossProfit);

    // 2. Profit (loss) from operating activities
    // = Gross Profit + Other income - Selling/distribution - Administrative - R&D - Other expenses
    const otherIncome = getFieldValue(`${prefix}:OtherIncome`, suffix);
    const sellingDistributionExpenses = getFieldValue(
      `${customPrefix}:SellingAndDistributionExpenses`,
      suffix,
    );
    const adminExpenses = getFieldValue(
      `${prefix}:AdministrativeExpense`,
      suffix,
    );
    const rdExpenses = getFieldValue(
      `${prefix}:ResearchAndDevelopmentExpense`,
      suffix,
    );
    const otherExpenses = getFieldValue(
      `${prefix}:OtherExpenseByFunction`,
      suffix,
    );

    const profitFromOperations =
      grossProfit +
      otherIncome -
      sellingDistributionExpenses -
      adminExpenses -
      rdExpenses -
      otherExpenses;
    // MPERS uses ssmt-mpers prefix for ProfitLossFromOperatingActivities
    const profitLossFromOpsPrefix =
      prefix === 'ifrs-full' ? prefix : customPrefix;
    setValue(
      `${profitLossFromOpsPrefix}:ProfitLossFromOperatingActivities`,
      suffix,
      profitFromOperations,
    );

    // 3. Profit (loss) before tax
    // = Profit from operating activities + Finance income - Finance costs
    //   + Share of profit/loss of associates and joint ventures
    //   + Fair valuation gain/loss (MFRS only)
    const financeIncome = getFieldValue(`${prefix}:FinanceIncome`, suffix);
    const financeCosts = getFieldValue(`${prefix}:FinanceCosts`, suffix);
    const shareOfAssociatesAndJVs = getFieldValue(
      `${prefix}:ShareOfProfitLossOfAssociatesAndJointVenturesAccountedForUsingEquityMethod`,
      suffix,
    );
    const fairValuation = getFieldValue(
      `${customPrefix}:FairValuationGainLossArisingFromDistributionOfNoncashAssetsToOwners`,
      suffix,
    );

    const profitBeforeTax =
      profitFromOperations +
      financeIncome -
      financeCosts +
      shareOfAssociatesAndJVs +
      fairValuation;
    setValue(`${prefix}:ProfitLossBeforeTax`, suffix, profitBeforeTax);

    // 4. Profit (loss) from continuing operations
    // = Profit before tax - Tax expense - Contribution to zakat
    const taxExpense = getFieldValue(
      `${prefix}:IncomeTaxExpenseContinuingOperations`,
      suffix,
    );
    const zakatContribution = getFieldValue(
      `${customPrefix}:ContributionOfZakat`,
      suffix,
    );

    const profitFromContinuingOps =
      profitBeforeTax - taxExpense - zakatContribution;
    setValue(
      `${prefix}:ProfitLossFromContinuingOperations`,
      suffix,
      profitFromContinuingOps,
    );

    // 5. Profit (loss)
    // = Profit from continuing operations + Profit from discontinued operations
    const profitFromDiscontinuedOps = getFieldValue(
      `${prefix}:ProfitLossFromDiscontinuedOperations`,
      suffix,
    );

    const profitLoss = profitFromContinuingOps + profitFromDiscontinuedOps;
    setValue(`${prefix}:ProfitLoss`, suffix, profitLoss);

    // 6. Total Profit (loss) Attribution (ProfitLoss_2)
    // = Owners of parent + Equity other components + Non-controlling interests
    const profitToOwners = getFieldValue(
      `${prefix}:ProfitLossAttributableToOwnersOfParent`,
      suffix,
    );
    const profitToEquityOther = getFieldValue(
      `${customPrefix}:ProfitLossAttributableToOtherComponentsOfEquity`,
      suffix,
    );
    const profitToNCI = getFieldValue(
      `${prefix}:ProfitLossAttributableToNoncontrollingInterests`,
      suffix,
    );

    const totalAttribution =
      profitToOwners + profitToEquityOther + profitToNCI;
    setValue(`${prefix}:ProfitLoss_2`, suffix, totalAttribution);

    // 7. Basic Earnings Per Share Total
    // = Basic EPS from continuing + Basic EPS from discontinued
    const basicEPSContinuing = getFieldValue(
      `${prefix}:BasicEarningsLossPerShareFromContinuingOperations`,
      suffix,
    );
    const basicEPSDiscontinued = getFieldValue(
      `${prefix}:BasicEarningsLossPerShareFromDiscontinuedOperations`,
      suffix,
    );
    const basicEPSTotal = basicEPSContinuing + basicEPSDiscontinued;
    setValue(`${prefix}:BasicEarningsLossPerShare`, suffix, basicEPSTotal);

    // 8. Diluted Earnings Per Share Total
    // = Diluted EPS from continuing + Diluted EPS from discontinued
    const dilutedEPSContinuing = getFieldValue(
      `${prefix}:DilutedEarningsLossPerShareFromContinuingOperations`,
      suffix,
    );
    const dilutedEPSDiscontinued = getFieldValue(
      `${prefix}:DilutedEarningsLossPerShareFromDiscontinuedOperations`,
      suffix,
    );
    const dilutedEPSTotal = dilutedEPSContinuing + dilutedEPSDiscontinued;
    setValue(
      `${prefix}:DilutedEarningsLossPerShare`,
      suffix,
      dilutedEPSTotal,
    );
  });

  return calculatedTotals;
}
